/**
 * RectEntity - 사각형 도형 Entity
 */
import { SHAPES } from '../../../const'
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d } from '../../../types'
import type { IGroup, IRect, IRenderer } from '../../../backend/IRenderer'

const SHAPE_HIT_STROKE_WIDTH = 20

/**
 * Rect 생성 설정
 */
export interface RectCreateConfig {
  color: string
  width?: number
  height?: number
}

/**
 * Rect 초기화 설정
 */
export interface RectInitConfig {
  id?: string
  createPoint: Vector2d
  balloonPoint?: Vector2d
  config: RectCreateConfig
  rotation?: number
  layerId?: string
  isFill?: boolean
  shapeType?: 'line' | 'dash' | 'cloud'
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
  interactionMode?: boolean
}

/**
 * Rect 도형 생성 함수
 * @param canvasEngine - CanvasEngine 컨트롤러
 * @param initConfig - 초기화 설정
 * @returns Rect 관련 노드 핸들
 */
export function createRectShape(
  renderer: IRenderer,
  canvasEngine: ICanvasEngineEntityController,
  initConfig: RectInitConfig,
): {
  shape: IRect
  shapeWrapper: IGroup
  color: string
} {
  const { config } = initConfig
  const lineWidth = canvasEngine.areaLineWidth
  const color = config.color

  // Rect 생성
  const rect = renderer.createRect({
    x: 0,
    y: 0,
    width: config.width || 100,
    height: config.height || 100,
    stroke: color,
    strokeWidth: lineWidth,
    // 외곽 +패딩 사각형 hit + hitStrokeWidth 패딩
    hitArea: { kind: 'rectPad', pad: SHAPE_HIT_STROKE_WIDTH },
  })

  // Wrapper 그룹 생성
  const shapeWrapper = renderer.createGroup({
    x: 0,
    y: 0,
  })
  shapeWrapper.add(rect)

  return {
    shape: rect,
    shapeWrapper,
    color,
  }
}

/**
 * Rect Entity export 데이터
 */
export interface RectExportData {
  type: typeof SHAPES.RECT
  shapeId: string
  point: Vector2d
  balloonPoint: Vector2d
  color: string
  layerId: string
  width?: number
  height?: number
  rotate?: number
  isFill?: boolean
  rectType?: 'line' | 'dash' | 'cloud'
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}

/**
 * Rect Entity를 export 데이터로 변환
 */
export function exportRectData(entity: {
  id: string
  position: Vector2d
  balloonPosition: Vector2d
  color: string
  layerId: string
  width?: number
  height?: number
  rotation?: number
  isFill?: boolean
  rectType?: 'line' | 'dash' | 'cloud'
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}): RectExportData {
  return {
    type: SHAPES.RECT,
    shapeId: entity.id,
    point: entity.position,
    balloonPoint: entity.balloonPosition,
    color: entity.color,
    layerId: entity.layerId,
    width: entity.width,
    height: entity.height,
    rotate: entity.rotation,
    isFill: entity.isFill,
    rectType: entity.rectType,
    balloonFontColor: entity.balloonFontColor,
    balloonFillColor: entity.balloonFillColor,
    balloonScale: entity.balloonScale,
    connectedLineThicknessScale: entity.connectedLineThicknessScale,
    balloonFontScale: entity.balloonFontScale,
    pointShapeScale: entity.pointShapeScale,
    areaBorderThicknessScale: entity.areaBorderThicknessScale,
  }
}
